import type { AgentKind } from '@cm/protocol';

/**
 * Marcas dos agentes, desenhadas aqui em SVG.
 *
 * Claude, Codex e Gemini são interpretações próprias, o bastante para
 * reconhecer o agente. O Grok usa o glifo público do grok.com (o redemoinho),
 * desenhado aqui em vetor e pintado na cor do tema, para continuar nítido
 * offline em qualquer tamanho.
 */

/**
 * A cor de cada agente vem do tema, pelos tokens `--claude` / `--codex` /
 * `--gemini` / `--grok`.
 *
 * Antes isto era um mapa de hex fixo. O problema nao era a duplicacao: era o
 * prata do Codex, que e quase branco e desaparece num tema claro. Quem precisa
 * do valor em hex de verdade -- so o xterm, que nao le CSS -- usa o
 * `agentColor(tokens, agent)` do theme.ts.
 */
export function agentCssColor(agent: AgentKind): string {
  return `var(--${agent})`;
}

export const AGENT_LABELS: Record<AgentKind, string> = {
  claude: 'Claude Code',
  codex: 'Codex',
  gemini: 'Gemini',
  grok: 'Grok Build',
};

interface Props {
  agent: AgentKind;
  size?: number;
  className?: string;
}

/** Explosão de raios, na linha da identidade do Claude. */
function ClaudeMark({ size }: { size: number }) {
  // Raios alternando comprimento longo/curto, como pétalas de um asterisco.
  const rays = Array.from({ length: 12 }, (_, i) => {
    const angle = (i * Math.PI * 2) / 12;
    const inner = 2.4;
    const outer = i % 2 === 0 ? 8.6 : 6.2;
    return {
      x1: 12 + Math.cos(angle) * inner,
      y1: 12 + Math.sin(angle) * inner,
      x2: 12 + Math.cos(angle) * outer,
      y2: 12 + Math.sin(angle) * outer,
      w: i % 2 === 0 ? 2.5 : 1.7,
    };
  });

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {rays.map((r, i) => (
        <line
          key={i}
          x1={r.x1}
          y1={r.y1}
          x2={r.x2}
          y2={r.y2}
          stroke="currentColor"
          strokeWidth={r.w}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** Nó hexagonal, evocando a marca da OpenAI sem copiá-la. */
function CodexMark({ size }: { size: number }) {
  const hex = (r: number) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = (i * Math.PI) / 3 - Math.PI / 2;
      return `${(12 + Math.cos(a) * r).toFixed(2)},${(12 + Math.sin(a) * r).toFixed(2)}`;
    }).join(' ');

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <polygon
        points={hex(9)}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <polygon
        points={hex(4.6)}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
        opacity="0.75"
      />
      {/* Três raios ligando os anéis, dando a sensação de nó entrelaçado. */}
      {[0, 2, 4].map((i) => {
        const a = (i * Math.PI) / 3 - Math.PI / 2;
        return (
          <line
            key={i}
            x1={12 + Math.cos(a) * 4.6}
            y1={12 + Math.sin(a) * 4.6}
            x2={12 + Math.cos(a) * 9}
            y2={12 + Math.sin(a) * 9}
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            opacity="0.75"
          />
        );
      })}
    </svg>
  );
}

/** Brilho de quatro pontas, na pegada do Gemini. */
function GeminiMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      {/* Estrela de quatro pontas com laterais côncavas. */}
      <path
        d="M12 1.6c.9 4.7 2.9 7.8 8.4 10.4-5.5 2.6-7.5 5.7-8.4 10.4-.9-4.7-2.9-7.8-8.4-10.4C9.1 9.4 11.1 6.3 12 1.6Z"
        fill="currentColor"
      />
      {/* Brilho menor, deslocado, para dar profundidade. */}
      <path
        d="M19.1 15.6c.35 1.85 1.15 3.05 3.3 4.1-2.15 1.05-2.95 2.25-3.3 4.1-.35-1.85-1.15-3.05-3.3-4.1 2.15-1.05 2.95-2.25 3.3-4.1Z"
        fill="currentColor"
        opacity="0.45"
        transform="translate(-1.4 -2.2) scale(0.78) translate(4 4)"
      />
    </svg>
  );
}

/**
 * Redemoinho do Grok, o glifo que o grok.com usa ao lado do nome.
 * Os dois contornos são o desenho público, só reenquadrados num quadrado
 * e preenchidos com `currentColor` para seguir `--grok`.
 */
function GrokMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 -0.5 34 34" aria-hidden="true">
      <path
        fill="currentColor"
        d="M13.237 21.041 24.319 12.851c.543-.402 1.32-.245 1.578.378 1.363 3.29.754 7.242-1.957 9.956-2.71 2.714-6.482 3.309-9.93 1.954l-3.765 1.745c5.401 3.697 11.96 2.782 16.059-1.324 3.251-3.255 4.258-7.692 3.316-11.693l.009.009c-1.366-5.878.336-8.227 3.82-13.031l.247-.345-4.585 4.591-.001-.015L13.234 21.044"
      />
      <path
        fill="currentColor"
        d="M10.95 23.031c-3.877-3.708-3.208-9.446.1-12.755 2.446-2.449 6.453-3.449 9.952-1.979l3.757-1.737c-.677-.49-1.544-1.017-2.54-1.387-4.5-1.854-9.887-.931-13.545 2.728-3.518 3.523-4.625 8.939-2.725 13.561 1.42 3.454-.907 5.898-3.251 8.364-.83.874-1.664 1.748-2.335 2.674l10.584-9.466"
      />
    </svg>
  );
}

export function AgentMark({ agent, size = 16, className }: Props) {
  const style = { color: agentCssColor(agent), display: 'block', flexShrink: 0 };
  return (
    <span className={className} style={style} title={AGENT_LABELS[agent]}>
      {agent === 'claude' && <ClaudeMark size={size} />}
      {agent === 'codex' && <CodexMark size={size} />}
      {agent === 'gemini' && <GeminiMark size={size} />}
      {agent === 'grok' && <GrokMark size={size} />}
    </span>
  );
}
